import { useState } from 'react';
import { apiClient, PrepareUploadResponse } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';
import { toast } from 'sonner';

export interface UploadedFile {
  file: File;
  attachmentId?: string;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  progress: number;
  error?: string;
  uploadData?: PrepareUploadResponse;
}

export function useStorage() {
  const [uploads, setUploads] = useState<UploadedFile[]>([]);

  const addFiles = (files: File[]) => {
    const newUploads = files.map((file) => ({
      file,
      status: 'pending' as const,
      progress: 0,
    }));
    setUploads((prev) => [...prev, ...newUploads]);
    
    newUploads.forEach((upload) => uploadFile(upload));
  };

  const removeFile = async (index: number) => {
    const fileToRemove = uploads[index];
    
    if (fileToRemove.status === 'completed' && fileToRemove.attachmentId) {
      try {
        await apiClient.deleteFile(fileToRemove.attachmentId);
      } catch (error) {
        console.error('Failed to delete file from server:', error);
      }
    }

    setUploads((prev) => prev.filter((_, i) => i !== index));
  };

  const uploadFile = async (upload: UploadedFile) => {
    updateUploadState(upload.file, { status: 'uploading', progress: 0 });

    try {
      const prepareResponse = await apiClient.prepareUpload({
        fileName: upload.file.name,
        fileType: upload.file.type,
        fileSize: upload.file.size,
      });

      updateUploadState(upload.file, { 
        uploadData: prepareResponse,
        attachmentId: prepareResponse.attachmentId 
      });

      // 2. Upload to GCP (PUT to signed URL)
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', prepareResponse.uploadUrl, true);
      
      // Add required headers
      Object.entries(prepareResponse.headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value);
      });

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const progress = Math.round((e.loaded / e.total) * 100);
          updateUploadState(upload.file, { progress });
        }
      };

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // 3. Verify upload (optional but good for consistency)
          try {
            const verified = await apiClient.verifyUpload(prepareResponse.attachmentId);
            if (verified) {
              updateUploadState(upload.file, { status: 'completed', progress: 100 });
            } else {
              throw new Error('Upload verification failed');
            }
          } catch (err: unknown) {
            updateUploadState(upload.file, { status: 'error', error: 'Verification failed' });
          }
        } else {
          updateUploadState(upload.file, { status: 'error', error: 'Upload failed' });
        }
      };

      xhr.onerror = () => {
        updateUploadState(upload.file, { status: 'error', error: 'Network error' });
      };

      xhr.send(upload.file);

    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error, "Upload failed");
      console.error('Upload error:', error);
      updateUploadState(upload.file, { status: 'error', error: errorMessage });
      toast.error(`Failed to upload ${upload.file.name}: ${errorMessage}`);
    }
  };

  const updateUploadState = (file: File, updates: Partial<UploadedFile>) => {
    setUploads((prev) => 
      prev.map((u) => (u.file === file ? { ...u, ...updates } : u))
    );
  };

  const clearUploads = () => setUploads([]);

  return {
    uploads,
    addFiles,
    removeFile,
    clearUploads,
    isUploading: uploads.some((u) => u.status === 'uploading'),
  };
}
